// Local notifications + activity store. Most of this lives in localStorage —
// fast, doesn't need a DB round-trip per render.
//
// Mini-spec #7 added a server-side path for cron-driven content (the
// daily morning briefing). Those rows live in public.user_notifications
// and merge into the same panel via fetchServerNotifications() below.

import { supabase } from './supabase';

const NOTIF_KEY = (userId) => `episode_notifs_${userId || 'guest'}`;
const ACT_KEY   = (userId) => `episode_activity_${userId || 'guest'}`;
const PREF_KEY  = (userId) => `episode_notif_prefs_${userId || 'guest'}`;

// Default notification preferences — all on.
export const DEFAULT_PREFS = {
  product_updates:    true,
  episode_tips:       true,
  trending_alerts:    true,
  sponsorship_offers: true,
  guest_requests:     true,
  follows:            true,
};

export const PREF_META = [
  { key: 'product_updates',    label: 'Product updates',      kind: 'notification', desc: 'New features, tips from the team.' },
  { key: 'episode_tips',       label: 'Episode tips',         kind: 'notification', desc: 'Suggestions to improve your drafts.' },
  { key: 'trending_alerts',    label: 'Trending alerts',      kind: 'notification', desc: 'When a topic in your niche is surging.' },
  { key: 'sponsorship_offers', label: 'Sponsorship offers',   kind: 'activity',     desc: 'Brands matching your niche + audience.' },
  { key: 'guest_requests',     label: 'Guest requests',       kind: 'activity',     desc: 'Creators asking to appear on your show.' },
  { key: 'follows',            label: 'Follows',              kind: 'activity',     desc: 'New listeners who started following you.' },
];

// ── Reads ─────────────────────────────────────────────────────────────────

export function readNotifications(userId) {
  try { return JSON.parse(localStorage.getItem(NOTIF_KEY(userId))) || []; } catch { return []; }
}

export function readActivity(userId) {
  try { return JSON.parse(localStorage.getItem(ACT_KEY(userId))) || []; } catch { return []; }
}

export function readPrefs(userId) {
  try {
    const raw = JSON.parse(localStorage.getItem(PREF_KEY(userId))) || {};
    return { ...DEFAULT_PREFS, ...raw };
  } catch { return { ...DEFAULT_PREFS }; }
}

// ── Writes ────────────────────────────────────────────────────────────────

function writeNotifications(userId, list) {
  try { localStorage.setItem(NOTIF_KEY(userId), JSON.stringify(list)); } catch {}
}
function writeActivity(userId, list) {
  try { localStorage.setItem(ACT_KEY(userId), JSON.stringify(list)); } catch {}
}
export function writePrefs(userId, prefs) {
  try { localStorage.setItem(PREF_KEY(userId), JSON.stringify(prefs)); } catch {}
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function dismissNotification(userId, id) {
  const next = readNotifications(userId).filter(n => n.id !== id);
  writeNotifications(userId, next);
  return next;
}

export function dismissActivity(userId, id) {
  const next = readActivity(userId).filter(n => n.id !== id);
  writeActivity(userId, next);
  return next;
}

export function markAllNotificationsRead(userId) {
  const next = readNotifications(userId).map(n => ({ ...n, unread: false }));
  writeNotifications(userId, next);
  return next;
}

// ── First-run seed ────────────────────────────────────────────────────────

export function seedIfEmpty(userId, profile) {
  let notifs = readNotifications(userId);
  if (notifs.length === 0) {
    const name = profile?.full_name?.split(' ')[0] || 'there';
    notifs = [
      {
        id: 'welcome',
        title: `Welcome, ${name} 👋`,
        body: 'Epio is built for creators who want to move before the trend hits. Drop a topic, hit Build, and we do the rest.',
        ts: Date.now(),
        unread: true,
      },
    ];
    writeNotifications(userId, notifs);
  }
  return notifs;
}

// ── Counts ────────────────────────────────────────────────────────────────

export function unreadCount(userId) {
  return readNotifications(userId).filter(n => n.unread).length
       + readActivity(userId).filter(a => a.unread).length;
}

// ── Pitch-seen bookmark ───────────────────────────────────────────────────
// The Inbox badge compares the newest incoming-pitch timestamp against this
// bookmark. The bookmark advances whenever the user actually opens the Inbox,
// so the badge clears naturally after they read.

const PITCH_SEEN_KEY = (userId) => `episode_pitch_seen_${userId || 'guest'}`;

export function readPitchSeen(userId) {
  try { return localStorage.getItem(PITCH_SEEN_KEY(userId)) || ''; }
  catch { return ''; }
}

export function markPitchesSeen(userId, latestIso) {
  if (!userId || !latestIso) return;
  try { localStorage.setItem(PITCH_SEEN_KEY(userId), latestIso); } catch {}
}

/**
 * Given the list of incoming pitches already loaded from Supabase, return
 * how many arrived after the user's last Inbox visit. Callers pass in the
 * rows so we don't re-hit the network.
 */
export function countUnreadPitches(userId, incomingRows = []) {
  if (!userId || !incomingRows?.length) return 0;
  const seen = readPitchSeen(userId);
  if (!seen) return incomingRows.length;
  return incomingRows.filter(r => r.created_at && r.created_at > seen).length;
}

// ── Activity push ─────────────────────────────────────────────────────────
// Producer API used by the matching engine (and later: follows, sponsor
// offers) to drop a new row into the user's activity feed. Deduplicates on
// `id` so repeated computations don't stack.
export function pushActivity(userId, activity) {
  if (!userId || !activity?.id) return;
  const list = readActivity(userId);
  if (list.some(a => a.id === activity.id)) return;
  const next = [
    { unread: true, ts: Date.now(), ...activity },
    ...list,
  ].slice(0, 60); // cap history
  writeActivity(userId, next);
}

// ── Server-side notifications (mini-spec #7) ─────────────────────────────
// The cron writes to public.user_notifications. The client merges those
// rows into the same panel as the localStorage notifications. Read-only
// from the client perspective — only the cron writes.

/**
 * Fetch the user's server-side notifications (newest first). Returns an
 * empty array on any failure so the panel never crashes if Supabase is
 * misconfigured.
 *
 * Each row gets normalized to the same shape as a local notification so
 * NotificationsPanel can render them in one merged list.
 */
export async function fetchServerNotifications(userId, limit = 30) {
  if (!supabase || !userId) return [];
  try {
    const { data, error } = await supabase
      .from('user_notifications')
      .select('id, kind, title, body, payload, action_url, read_at, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) { console.warn('fetchServerNotifications:', error.message); return []; }
    return (data || []).map(row => ({
      id:        `srv:${row.id}`,
      serverId:  row.id,
      kind:      row.kind,
      title:     row.title,
      body:      row.body || '',
      payload:   row.payload || {},
      actionUrl: row.action_url || null,
      ts:        new Date(row.created_at).getTime(),
      unread:    !row.read_at,
      source:    'server',
      // Map the server kind onto an existing pref key so the prefs UI
      // can hide morning_briefing items if the user has trending_alerts off.
      prefKey:   row.kind === 'morning_briefing' ? 'trending_alerts' : null,
    }));
  } catch (err) {
    console.warn('fetchServerNotifications failed:', err?.message);
    return [];
  }
}

/** Mark a server-side notification as read. Idempotent. */
export async function markServerNotificationRead(userId, serverId) {
  if (!supabase || !userId || !serverId) return;
  try {
    await supabase
      .from('user_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', serverId)
      .eq('user_id', userId);
  } catch (err) {
    console.warn('markServerNotificationRead failed:', err?.message);
  }
}
